import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Building2, Upload } from "lucide-react";
import {
  validatePAN,
  validateTAN,
  validateGSTIN,
  validateCIN,
  validatePFRegistration,
  validateESIRegistration,
} from "@/lib/validations";
import { sanitizePhoneNumber } from "@/lib/utils";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const CompanySettingsTab = ({
  companySettings,
  handleCompanySettingChange,
  handleLogoUpload,
  removeLogo,
}) => {
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  const canEditCompanyConfig = canEdit("settings_company_configuration");
  
  const validation = {
    pan: validatePAN(companySettings.pan),
    tan: validateTAN(companySettings.tan),
    gstin: validateGSTIN(companySettings.gstin),
    cin: validateCIN(companySettings.cin),
    pf: validatePFRegistration(companySettings.pf_registration),
    esi: validateESIRegistration(companySettings.esi_registration),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Building2 className="h-5 w-5" />
          <span>Company Information</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Company Name */}
        <div id="setting-company-name">
          <Label>Company Name</Label>
          <Input
            disabled={!canEditCompanyConfig}
            placeholder="e.g., Element Tree Pvt. Ltd."
            value={companySettings.company_name}
            onChange={(e) =>
              handleCompanySettingChange("company_name", e.target.value)
            }
            data-testid="company-name-input"
          />
        </div>

        {/* Address */}
        <div id="setting-company-address">
          <Label>Address</Label>
          <Textarea
            disabled={!canEditCompanyConfig}
            placeholder="e.g., 501 Corporate Park, Andheri East, Mumbai"
            value={companySettings.address}
            onChange={(e) =>
              handleCompanySettingChange("address", e.target.value)
            }
            rows={3}
            data-testid="company-address-input"
          />
        </div>

        {/* Phone + Email */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Phone</Label>
            <Input
              disabled={!canEditCompanyConfig}
              placeholder="e.g., +91 9876543210"
              value={companySettings.phone}
              onChange={(e) =>
                handleCompanySettingChange(
                  "phone",
                  sanitizePhoneNumber(e.target.value),
                )
              }
              data-testid="company-phone-input"
            />
          </div>

          <div>
            <Label>Email</Label>
            <Input
              disabled={!canEditCompanyConfig}
              placeholder="e.g., info@elementtree.com"
              value={companySettings.email}
              onChange={(e) =>
                handleCompanySettingChange("email", e.target.value)
              }
              data-testid="company-email-input"
            />
          </div>
        </div>

        {/* Website */}
        <div>
          <Label>Website</Label>
          <Input
            disabled={!canEditCompanyConfig}
            placeholder="e.g., https://elementtree.com"
            value={companySettings.website}
            onChange={(e) =>
              handleCompanySettingChange("website", e.target.value)
            }
          />
        </div>

        {/* Company Logo */}
        <div id="setting-company-logo">
          <Label>Company Logo</Label>
          <div className="space-y-3">
            {companySettings.logo_url ? (
              <div className="flex items-center space-x-4 p-3 border rounded-lg">
                <img
                  src={companySettings.logo_url}
                  alt="Company Logo"
                  className="w-16 h-16 object-contain border rounded"
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    Logo uploaded successfully
                  </p>
                  <div className="flex space-x-2 mt-2">
                    <Button
                      size="sm"
                      disabled={!canEditCompanyConfig}
                      variant="outline"
                      onClick={() =>
                        document.getElementById("logo-upload").click()
                      }
                    >
                      Change Logo
                    </Button>
                    <Button
                      disabled={!canEditCompanyConfig}
                      size="sm"
                      variant="outline"
                      onClick={removeLogo}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Upload company logo
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canEditCompanyConfig}
                  onClick={() => document.getElementById("logo-upload").click()}
                >
                  Choose File
                </Button>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
              </div>
            )}
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
        </div>

        <Separator />

        {/* PAN / TAN */}
        <div className="grid grid-cols-2 gap-4">
          <div id="setting-company-pan">
            <Label>PAN Number</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.pan && companySettings.pan ? "border-red-500" : ""
              }
              placeholder="e.g., AAAPL1234C"
              value={companySettings.pan}
              onChange={(e) =>
                handleCompanySettingChange("pan", e.target.value.toUpperCase())
              }
            />
            {!validation.pan && companySettings.pan && (
              <p className="text-red-500 text-xs mt-1">Invalid PAN format</p>
            )}
          </div>

          <div>
            <Label>TAN Number</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.tan && companySettings.tan ? "border-red-500" : ""
              }
              placeholder="e.g., MUMA12345B"
              value={companySettings.tan}
              onChange={(e) =>
                handleCompanySettingChange("tan", e.target.value.toUpperCase())
              }
            />
            {!validation.tan && companySettings.tan && (
              <p className="text-red-500 text-xs mt-1">Invalid TAN format</p>
            )}
          </div>
        </div>

        {/* GSTIN / CIN */}
        <div className="grid grid-cols-2 gap-4">
          <div id="setting-company-gstin">
            <Label>GSTIN</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.gstin && companySettings.gstin
                  ? "border-red-500"
                  : ""
              }
              placeholder="e.g., 27ABCDE1234F1Z5"
              value={companySettings.gstin}
              onChange={(e) =>
                handleCompanySettingChange(
                  "gstin",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {!validation.gstin && companySettings.gstin && (
              <p className="text-red-500 text-xs mt-1">Invalid GSTIN format</p>
            )}
          </div>

          <div>
            <Label>CIN</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.cin && companySettings.cin ? "border-red-500" : ""
              }
              placeholder="e.g., U12345MH2024PTC123456"
              value={companySettings.cin}
              onChange={(e) =>
                handleCompanySettingChange("cin", e.target.value.toUpperCase())
              }
            />
            {!validation.cin && companySettings.cin && (
              <p className="text-red-500 text-xs mt-1">Invalid CIN format</p>
            )}
          </div>
        </div>

        {/* PF / ESI */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>PF Registration</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.pf && companySettings.pf_registration
                  ? "border-red-500"
                  : ""
              }
              placeholder="e.g., MH/PF/123456"
              value={companySettings.pf_registration}
              onChange={(e) =>
                handleCompanySettingChange(
                  "pf_registration",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {!validation.pf && companySettings.pf_registration && (
              <p className="text-red-500 text-xs mt-1">
                Invalid PF registration format
              </p>
            )}
          </div>

          <div>
            <Label>ESI Registration</Label>
            <Input
              disabled={!canEditCompanyConfig}
              className={
                !validation.esi && companySettings.esi_registration
                  ? "border-red-500"
                  : ""
              }
              placeholder="e.g., 12345678901234567"
              value={companySettings.esi_registration}
              onChange={(e) =>
                handleCompanySettingChange(
                  "esi_registration",
                  e.target.value.toUpperCase(),
                )
              }
            />
            {!validation.esi && companySettings.esi_registration && (
              <p className="text-red-500 text-xs mt-1">
                Invalid ESI registration format
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySettingsTab;
